export type RegisterIframeOptions = {
  /** Path-only login link shown after the registration form. */
  loginPath?: string;
  /** Regional site hostname (`data-pq-domain`); defaults to the page origin when omitted. */
  siteDomain?: string;
  /** Override embed base (defaults to script origin or `window.__PQ_FORMS_PROXY_URL__`). */
  embedBase?: string;
  /** Minimum iframe height in px. */
  minHeight?: number;
};

const RESIZE_MESSAGE = 'pq-register-resize';

const LOADER_SCRIPT_ORIGIN = ((): string => {
  const script = document.currentScript;
  if (!script?.src) {
    return '';
  }
  try {
    return new URL(script.src).origin;
  } catch {
    return '';
  }
})();

function resolveEmbedBase(explicit?: string): string {
  const fromOption = explicit?.trim().replace(/\/+$/, '');
  if (fromOption) {
    return fromOption;
  }
  const fromWindow = window.__PQ_FORMS_PROXY_URL__?.trim().replace(/\/+$/, '');
  if (fromWindow) {
    return fromWindow;
  }
  if (LOADER_SCRIPT_ORIGIN) {
    return LOADER_SCRIPT_ORIGIN;
  }
  return '';
}

function normalizeSiteOrigin(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) {
    return null;
  }
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(withScheme).origin;
  } catch {
    return null;
  }
}

function resolveSiteOrigin(
  siteDomain: string | null | undefined,
  fallbackOrigin: string = window.location.origin,
): string {
  return normalizeSiteOrigin(siteDomain) ?? fallbackOrigin;
}

function resolveSiteUrl(path: string, siteOrigin: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${siteOrigin}${normalized}`;
}

function mount(host: HTMLElement, options: RegisterIframeOptions = {}): HTMLIFrameElement {
  const loginPath =
    options.loginPath?.trim() ||
    host.getAttribute('data-pq-login')?.trim() ||
    '/login';
  const siteOrigin = resolveSiteOrigin(
    options.siteDomain ?? host.getAttribute('data-pq-domain') ?? undefined,
  );
  const embedBase = resolveEmbedBase(options.embedBase);
  if (!embedBase) {
    throw new Error(
      'PQRegisterIframe: set data-pq-embed-base on the host, window.__PQ_FORMS_PROXY_URL__, or load register-iframe.js from the proxy origin.',
    );
  }
  const minHeight = options.minHeight ?? 680;

  const params = new URLSearchParams({ site_domain: siteOrigin });
  const normalizedLogin = loginPath.startsWith('/') ? loginPath.slice(1) : loginPath;
  if (normalizedLogin) {
    params.set('login', normalizedLogin);
    params.set('login_url', resolveSiteUrl(loginPath, siteOrigin));
  }

  const iframe = document.createElement('iframe');
  iframe.title = 'PQ Online registration';
  iframe.src = `${embedBase}/embed/register.html?${params.toString()}`;
  iframe.loading = 'lazy';
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  iframe.style.width = '100%';
  iframe.style.border = '0';
  iframe.style.display = 'block';
  iframe.style.minHeight = `${String(minHeight)}px`;
  iframe.style.overflow = 'hidden';
  iframe.setAttribute('scrolling', 'no');

  host.replaceChildren(iframe);
  return iframe;
}

function wireResize(host: HTMLElement, iframe: HTMLIFrameElement, minHeight: number): void {
  const onMessage = (event: MessageEvent): void => {
    if (event.source !== iframe.contentWindow) {
      return;
    }
    const data = event.data as { type?: string; height?: number } | null;
    if (!data || data.type !== RESIZE_MESSAGE || typeof data.height !== 'number') {
      return;
    }
    const next = Math.max(minHeight, Math.ceil(data.height));
    iframe.style.height = `${String(next)}px`;
  };
  window.addEventListener('message', onMessage);
}

function autoInit(): void {
  const script = document.currentScript;
  const scriptLogin = script?.getAttribute('data-pq-login');
  const scriptBase = script?.getAttribute('data-pq-embed-base') ?? undefined;

  const hosts = document.querySelectorAll<HTMLElement>('[data-pq-register-iframe]');
  hosts.forEach((host) => {
    if (host.dataset.pqRegisterIframeWired === '1') {
      return;
    }
    host.dataset.pqRegisterIframeWired = '1';
    const minHeight = Number(host.getAttribute('data-pq-min-height') ?? '680') || 680;
    const iframe = mount(host, {
      loginPath: host.getAttribute('data-pq-login') ?? scriptLogin ?? undefined,
      siteDomain: host.getAttribute('data-pq-domain') ?? undefined,
      embedBase: host.getAttribute('data-pq-embed-base') ?? scriptBase,
      minHeight,
    });
    wireResize(host, iframe, minHeight);
  });
}

export type PQRegisterIframeApi = {
  mount: typeof mount;
};

declare global {
  interface Window {
    PQRegisterIframe?: PQRegisterIframeApi;
    __PQ_FORMS_PROXY_URL__?: string;
  }
}

window.PQRegisterIframe = { mount };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}
